"use client";

import { ReactNode, useEffect, useRef } from "react";
import { useAccountStore } from "@/hooks/use-account-store";
import { authClient } from "@/lib/auth-client";
import { Session, User } from "@/prisma/generated/client";

export function AccountProvider({ children }: { children: ReactNode }) {
  const initialize = useAccountStore(state => state.initialize);
  const setSession = useAccountStore(state => state.setSession);
  const setUser = useAccountStore(state => state.setUser);
  const setIsPending = useAccountStore(state => state.setIsPending);

  // Sync session and active organizations from better-auth
  const { data: sessionData, isPending } = authClient.useSession();

  // Sync session data to the account store
  useEffect(() => {
    setIsPending(isPending);
    if (isPending) return;
    setSession((sessionData?.session as Session) ?? null);
    setUser((sessionData?.user as User) ?? null);
  }, [sessionData, isPending]);

  // Fetch active location when the session identity changes. better-auth's
  // useSession hands back a NEW object on every refetch (signal fires,
  // freshness re-validation, multi-tab, etc.) even when nothing changed, so
  // we gate on the session id — re-running initialize() on every refetch
  // would re-toggle the store's `isLoading` and blank guarded pages.
  const lastSessionId = useRef<string | null>(null);
  useEffect(() => {
    const id = sessionData?.session?.id ?? null;
    if (!id || id === lastSessionId.current) return;
    lastSessionId.current = id;
    void initialize().catch(() => {
      // initialize handles recovery internally; swallow to avoid unhandled rejection
    });
  }, [sessionData?.session?.id, initialize]);

  return children;
}
